import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  User, Users, GraduationCap, Plus, X, ChevronRight, ChevronLeft, 
  Shield, Check, Phone, KeyRound, ArrowLeft, CheckCircle2, Eye, EyeOff,
  Sparkles, UserPlus, Trash2, Clock, Mail, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EdpiesLogo from '@/components/EdpiesLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Year groups
const YEAR_GROUPS = [
  { value: 'year-1', label: 'Year 1', age: '5-6' },
  { value: 'year-2', label: 'Year 2', age: '6-7' },
  { value: 'year-3', label: 'Year 3', age: '7-8' },
  { value: 'year-4', label: 'Year 4', age: '8-9' },
  { value: 'year-5', label: 'Year 5', age: '9-10' },
  { value: 'year-6', label: 'Year 6', age: '10-11' },
  { value: 'year-7', label: 'Year 7', age: '11-12' },
  { value: 'year-8', label: 'Year 8', age: '12-13' },
  { value: 'year-9', label: 'Year 9', age: '13-14' },
  { value: 'year-10', label: 'Year 10', age: '14-15' },
  { value: 'year-11', label: 'Year 11', age: '15-16' },
  { value: 'year-12', label: 'Year 12', age: '16-17' },
  { value: 'year-13', label: 'Year 13', age: '17-18' },
];

// Learning goals
const LEARNING_GOALS = [
  { value: 'exam-prep', label: 'Preparing for an exam', description: 'Focus on specific exam preparation' },
  { value: 'improve-skills', label: 'Improve general skills', description: 'Build overall academic skills' },
];

// Exam options
const EXAM_OPTIONS = [
  { value: '7plus', label: '7+ Entry' },
  { value: '11plus', label: '11+ Entry' },
  { value: 'gcse', label: 'GCSE' },
  { value: 'a-level', label: 'A-Level' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Prefer not to say' },
];

const NewAuth = ({ setProgress }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('signin');
  const [registrationStep, setRegistrationStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Sign In State
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Registration type
  const [registrationType, setRegistrationType] = useState(''); // 'parent' or 'student'
  
  // Parent Registration State
  const [parentData, setParentData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    postcode: '',
    password: '',
    confirmPassword: '',
  });
  
  // Students State (array for multiple children)
  const [students, setStudents] = useState([{
    id: 1,
    username: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    yearGroup: '',
    gender: '',
    learningGoal: '',
    examTarget: '',
  }]);
  
  // Tutor Registration State
  const [tutorData, setTutorData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    subjects: [],
    qualifications: '',
    experience: '',
    bio: '',
  });
  const [newTutorSubject, setNewTutorSubject] = useState('');
  
  // Direct Student Registration (for A-Level etc)
  const [directStudentData, setDirectStudentData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    postcode: '',
    username: '',
    password: '',
    confirmPassword: '',
    yearGroup: '',
    gender: '',
    learningGoal: '',
    examTarget: '',
  });
  
  // Terms
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Modals
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [registeredStudents, setRegisteredStudents] = useState([]);

  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Handle Sign In
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: signInEmail,
        password: signInPassword
      });
      
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData.user));
      localStorage.setItem('token', userData.token);
      
      setProgress({
        user: userData.user,
        isAuthenticated: true,
        favorites: userData.user.favorites || [],
        subscription: userData.user.subscription || null
      });
      
      toast.success('Welcome back!');
      
      // Navigate based on user type
      if (userData.user.isAdmin) {
        navigate('/admin/tutors');
      } else if (userData.user.userType === 'parent') {
        navigate('/parent-dashboard');
      } else if (userData.user.userType === 'student') {
        navigate('/welcome');
      } else {
        navigate('/welcome');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    }
    
    setLoading(false);
  };

  // Add new student
  const addStudent = () => {
    if (students.length >= 5) {
      toast.error('Maximum 5 children allowed per account');
      return;
    }
    setStudents([...students, {
      id: Date.now(),
      username: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      yearGroup: '',
      gender: '',
      learningGoal: '',
      examTarget: '',
    }]);
  };

  // Remove student
  const removeStudent = (id) => {
    if (students.length === 1) {
      toast.error('At least one student is required');
      return;
    }
    setStudents(students.filter(s => s.id !== id));
  };

  // Update student data
  const updateStudent = (id, field, value) => {
    setStudents(students.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  // Validate parent step
  const validateParentStep = () => {
    const { fullName, email, mobile, postcode, password, confirmPassword } = parentData;
    if (!fullName || !email || !mobile || !postcode || !password) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  // Validate students step
  const validateStudentsStep = () => {
    for (const student of students) {
      if (!student.username || !student.password || !student.fullName || !student.yearGroup || !student.gender || !student.learningGoal) {
        toast.error(`Please fill in all required fields for ${student.fullName || 'student'}`);
        return false;
      }
      if (student.password.length < 6) {
        toast.error(`Password for ${student.fullName} must be at least 6 characters`);
        return false;
      }
      if (student.password !== student.confirmPassword) {
        toast.error(`Passwords do not match for ${student.fullName}`);
        return false;
      }
      if (student.learningGoal === 'exam-prep' && !student.examTarget) {
        toast.error(`Please select an exam target for ${student.fullName}`);
        return false;
      }
    }
    return true;
  };

  // Validate direct student registration
  const validateDirectStudent = () => {
    const { fullName, email, password, confirmPassword, yearGroup, gender, learningGoal, examTarget } = directStudentData;
    if (!fullName || !email || !password || !yearGroup || !gender || !learningGoal) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (learningGoal === 'exam-prep' && !examTarget) {
      toast.error('Please select an exam target');
      return false;
    }
    return true;
  };

  // Handle parent registration with students
  const handleParentRegistration = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      // Register parent
      const parentResponse = await axios.post(`${API}/auth/register-parent`, {
        fullName: parentData.fullName,
        email: parentData.email,
        mobile: parentData.mobile,
        postcode: parentData.postcode,
        password: parentData.password,
        students: students.map(s => ({
          username: s.username,
          password: s.password,
          fullName: s.fullName,
          yearGroup: s.yearGroup,
          gender: s.gender,
          learningGoal: s.learningGoal,
          examTarget: s.examTarget || null,
        }))
      });
      
      const { parent, students: registeredStudentsList, pendingVerification, emailSent, message } = parentResponse.data;
      
      setRegisteredStudents(registeredStudentsList);
      
      if (pendingVerification) {
        // Show email verification pending modal
        setShowEmailVerificationModal(true);
        setVerificationEmail(parentData.email);

        // If the verification email failed to dispatch, warn the parent
        // immediately so they know to use "Resend" rather than waiting in vain.
        if (emailSent === false) {
          toast.warning(
            message || "We couldn't send the verification email — please use the Resend button.",
            { duration: 8000 }
          );
        } else {
          toast.success("Verification email sent — check your inbox (and spam folder).");
        }
      } else {
        // Legacy flow - direct login
        localStorage.setItem('user', JSON.stringify(parent));
        localStorage.setItem('token', parentResponse.data.token);
        
        setProgress({
          user: parent,
          isAuthenticated: true,
          favorites: [],
          subscription: parent.subscription || null
        });
        
        setShowSuccessModal(true);
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    }
    
    setLoading(false);
  };

  // Handle direct student registration
  const handleDirectStudentRegistration = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: directStudentData.fullName,
        email: directStudentData.email,
        password: directStudentData.password,
        userType: 'student',
        mobileNumber: directStudentData.mobile,
        studentProfile: {
          yearGroup: directStudentData.yearGroup,
          gender: directStudentData.gender,
          learningGoal: directStudentData.learningGoal,
          examTarget: directStudentData.examTarget,
        }
      });
      
      const userData = response.data;
      
      localStorage.setItem('user', JSON.stringify(userData.user));
      localStorage.setItem('token', userData.token);
      
      setProgress({
        user: userData.user,
        isAuthenticated: true,
        favorites: [],
        subscription: userData.user.subscription || null
      });
      
      if (userData.pendingApproval) {
        setShowPendingModal(true);
      } else {
        toast.success('Welcome to Edpies!');
        navigate('/welcome');
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    }
    
    setLoading(false);
  };

  // Handle next step
  const handleNextStep = () => {
    if (registrationType === 'parent') {
      if (registrationStep === 2 && !validateParentStep()) return;
      if (registrationStep === 3 && !validateStudentsStep()) return;
    } else if (registrationType === 'student') {
      if (registrationStep === 2 && !validateDirectStudent()) return;
    }
    setRegistrationStep(prev => prev + 1);
  };

  // Handle registration submission
  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    
    if (registrationStep === 1) {
      if (!registrationType) {
        toast.error('Please select who is registering');
        return;
      }
      setRegistrationStep(2);
      return;
    }
    
    if (registrationType === 'parent') {
      if (registrationStep === 2) {
        if (validateParentStep()) setRegistrationStep(3);
        return;
      }
      if (registrationStep === 3) {
        if (validateStudentsStep()) setRegistrationStep(4);
        return;
      }
      if (registrationStep === 4) {
        handleParentRegistration();
        return;
      }
    } else if (registrationType === 'student') {
      if (registrationStep === 2) {
        if (validateDirectStudent()) setRegistrationStep(3);
        return;
      }
      if (registrationStep === 3) {
        handleDirectStudentRegistration();
        return;
      }
    } else if (registrationType === 'tutor') {
      if (registrationStep === 2) {
        if (validateTutorForm()) setRegistrationStep(3);
        return;
      }
      if (registrationStep === 3) {
        handleTutorRegistration();
        return;
      }
    }
  };

  // Get total steps based on registration type
  const getTotalSteps = () => {
    if (registrationType === 'parent') return 4;
    if (registrationType === 'student') return 3;
    if (registrationType === 'tutor') return 3;
    return 4;
  };

  // Validate tutor form
  const validateTutorForm = () => {
    const { fullName, email, password, confirmPassword, subjects, qualifications, experience } = tutorData;
    if (!fullName || !email || !password || !qualifications || !experience) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (subjects.length === 0) {
      toast.error('Please add at least one subject');
      return false;
    }
    return true;
  };

  // Handle tutor registration
  const handleTutorRegistration = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: tutorData.fullName,
        email: tutorData.email,
        password: tutorData.password,
        userType: 'tutor',
        mobileNumber: tutorData.mobile,
        tutorProfile: {
          subjects: tutorData.subjects,
          qualifications: tutorData.qualifications,
          experience_years: parseInt(tutorData.experience) || 0,
          bio: tutorData.bio,
          hourly_rate: 40,
          availability: '',
          teaching_style: '',
        }
      });
      
      const userData = response.data;
      
      if (userData.pendingApproval) {
        setShowPendingModal(true);
      } else {
        localStorage.setItem('user', JSON.stringify(userData.user));
        localStorage.setItem('token', userData.token);
        
        setProgress({
          user: userData.user,
          isAuthenticated: true,
          favorites: [],
          subscription: userData.user.subscription || null
        });
        
        toast.success('Welcome to Edpies! Complete your profile to start tutoring.');
        navigate('/tutor/dashboard');
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    }
    
    setLoading(false);
  };

  // Forgot Password handlers
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Reset code generated!');
      if (response.data.reset_code) setResetCode(response.data.reset_code);
      setForgotPasswordStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate reset code.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotEmail,
        reset_code: resetCode,
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password.');
    }
    setLoading(false);
  };

  // Progress indicator
  const ProgressIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {Array.from({ length: getTotalSteps() }, (_, i) => i + 1).map((step) => (
        <div key={step} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
            step < registrationStep ? 'bg-green-500 text-white' 
              : step === registrationStep ? 'bg-purple-600 text-white ring-4 ring-purple-200' 
              : 'bg-gray-200 text-gray-500'
          }`}>
            {step < registrationStep ? <Check className="w-4 h-4" /> : step}
          </div>
          {step < getTotalSteps() && (
            <div className={`w-8 h-1 mx-1 rounded ${step < registrationStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  // Step 1: Who's registering
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Who's registering?</h3>
        <p className="text-gray-500 text-sm">Select how you'd like to register</p>
      </div>
      <div className="grid gap-3">
        <button
          type="button"
          onClick={() => setRegistrationType('parent')}
          data-testid="register-as-parent"
          className={`relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
            registrationType === 'parent' 
              ? 'border-purple-500 bg-purple-50 shadow-md' 
              : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
          }`}
        >
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800">Parent / Guardian</div>
            <div className="text-sm text-gray-500">Register and create accounts for your children</div>
          </div>
          {registrationType === 'parent' && (
            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </button>
        
        <button
          type="button"
          onClick={() => setRegistrationType('student')}
          data-testid="register-as-student"
          className={`relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
            registrationType === 'student' 
              ? 'border-purple-500 bg-purple-50 shadow-md' 
              : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
          }`}
        >
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800">Student (16+ / A-Level)</div>
            <div className="text-sm text-gray-500">Register yourself directly</div>
          </div>
          {registrationType === 'student' && (
            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </button>
        
        <button
          type="button"
          onClick={() => setRegistrationType('tutor')}
          data-testid="register-as-tutor"
          className={`relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
            registrationType === 'tutor' 
              ? 'border-purple-500 bg-purple-50 shadow-md' 
              : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
          }`}
        >
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center shadow">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800">Tutor / Educator</div>
            <div className="text-sm text-gray-500">Join our tutor marketplace</div>
          </div>
          {registrationType === 'tutor' && (
            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </button>
      </div>
    </div>
  );

  // Step 2: Parent Details
  const renderParentDetails = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Parent / Guardian Details</h3>
        <p className="text-gray-500 text-sm">Your account information</p>
      </div>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-sm">Full Name *</Label>
          <Input
            type="text"
            placeholder="Enter your full name"
            value={parentData.fullName}
            onChange={(e) => setParentData({ ...parentData, fullName: e.target.value })}
            data-testid="parent-fullname"
            className="h-10"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-sm">Email Address *</Label>
          <Input
            type="email"
            placeholder="your.email@example.com"
            value={parentData.email}
            onChange={(e) => setParentData({ ...parentData, email: e.target.value })}
            data-testid="parent-email"
            className="h-10"
          />
          <p className="text-xs text-gray-400">This will be your login username</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">Mobile Number *</Label>
            <Input
              type="tel"
              placeholder="+44 7XXX XXXXXX"
              value={parentData.mobile}
              onChange={(e) => setParentData({ ...parentData, mobile: e.target.value })}
              data-testid="parent-mobile"
              className="h-10"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Postcode *</Label>
            <Input
              type="text"
              placeholder="e.g., SW1A 1AA"
              value={parentData.postcode}
              onChange={(e) => setParentData({ ...parentData, postcode: e.target.value.toUpperCase() })}
              data-testid="parent-postcode"
              className="h-10"
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-sm">Password *</Label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Min. 6 characters"
              value={parentData.password}
              onChange={(e) => setParentData({ ...parentData, password: e.target.value })}
              data-testid="parent-password"
              className="h-10 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-sm">Confirm Password *</Label>
          <Input
            type="password"
            placeholder="Confirm your password"
            value={parentData.confirmPassword}
            onChange={(e) => setParentData({ ...parentData, confirmPassword: e.target.value })}
            data-testid="parent-confirm-password"
            className="h-10"
          />
        </div>
      </div>
    </div>
  );

  // Step 3: Student Details (for parent flow)
  const renderStudentDetails = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Student Details</h3>
        <p className="text-gray-500 text-sm">Create login credentials for your child(ren)</p>
      </div>
      
      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
        {students.map((student, index) => (
          <div key={student.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-purple-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Child {index + 1}
              </h4>
              {students.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeStudent(student.id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Student Username *</Label>
                <Input
                  type="text"
                  placeholder="e.g., johndoe123"
                  value={student.username}
                  onChange={(e) => updateStudent(student.id, 'username', e.target.value.toLowerCase().replace(/\s/g, ''))}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Full Name *</Label>
                <Input
                  type="text"
                  placeholder="Student's full name"
                  value={student.fullName}
                  onChange={(e) => updateStudent(student.id, 'fullName', e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Password *</Label>
                <Input
                  type="password"
                  placeholder="Min. 6 characters"
                  value={student.password}
                  onChange={(e) => updateStudent(student.id, 'password', e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Confirm Password *</Label>
                <Input
                  type="password"
                  placeholder="Confirm password"
                  value={student.confirmPassword}
                  onChange={(e) => updateStudent(student.id, 'confirmPassword', e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Year Group *</Label>
                <Select value={student.yearGroup} onValueChange={(v) => updateStudent(student.id, 'yearGroup', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {YEAR_GROUPS.map((y) => (
                      <SelectItem key={y.value} value={y.value}>{y.label} (Age {y.age})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Gender *</Label>
                <Select value={student.gender} onValueChange={(v) => updateStudent(student.id, 'gender', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs">Learning Goal *</Label>
              <RadioGroup
                value={student.learningGoal}
                onValueChange={(v) => updateStudent(student.id, 'learningGoal', v)}
                className="flex gap-4"
              >
                {LEARNING_GOALS.map((goal) => (
                  <div key={goal.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={goal.value} id={`${student.id}-${goal.value}`} />
                    <Label htmlFor={`${student.id}-${goal.value}`} className="text-xs font-normal cursor-pointer">
                      {goal.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            {student.learningGoal === 'exam-prep' && (
              <div className="space-y-1">
                <Label className="text-xs">Exam Target *</Label>
                <Select value={student.examTarget} onValueChange={(v) => updateStudent(student.id, 'examTarget', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select exam" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXAM_OPTIONS.map((e) => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <Button
        type="button"
        variant="outline"
        onClick={addStudent}
        className="w-full border-dashed border-purple-300 text-purple-600 hover:bg-purple-50"
      >
        <UserPlus className="w-4 h-4 mr-2" />
        Add Another Child
      </Button>
    </div>
  );

  // Step 2: Direct Student Registration
  const renderDirectStudentForm = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Student Registration</h3>
        <p className="text-gray-500 text-sm">Create your account</p>
      </div>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-sm">Full Name *</Label>
          <Input
            type="text"
            placeholder="Enter your full name"
            value={directStudentData.fullName}
            onChange={(e) => setDirectStudentData({ ...directStudentData, fullName: e.target.value })}
            className="h-10"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-sm">Email Address *</Label>
          <Input
            type="email"
            placeholder="your.email@example.com"
            value={directStudentData.email}
            onChange={(e) => setDirectStudentData({ ...directStudentData, email: e.target.value })}
            className="h-10"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">Mobile (Optional)</Label>
            <Input
              type="tel"
              placeholder="+44 7XXX XXXXXX"
              value={directStudentData.mobile}
              onChange={(e) => setDirectStudentData({ ...directStudentData, mobile: e.target.value })}
              className="h-10"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Postcode (Optional)</Label>
            <Input
              type="text"
              placeholder="e.g., SW1A 1AA"
              value={directStudentData.postcode}
              onChange={(e) => setDirectStudentData({ ...directStudentData, postcode: e.target.value.toUpperCase() })}
              className="h-10"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">Password *</Label>
            <Input
              type="password"
              placeholder="Min. 6 characters"
              value={directStudentData.password}
              onChange={(e) => setDirectStudentData({ ...directStudentData, password: e.target.value })}
              className="h-10"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Confirm Password *</Label>
            <Input
              type="password"
              placeholder="Confirm password"
              value={directStudentData.confirmPassword}
              onChange={(e) => setDirectStudentData({ ...directStudentData, confirmPassword: e.target.value })}
              className="h-10"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">Year Group *</Label>
            <Select 
              value={directStudentData.yearGroup} 
              onValueChange={(v) => setDirectStudentData({ ...directStudentData, yearGroup: v })}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {YEAR_GROUPS.map((y) => (
                  <SelectItem key={y.value} value={y.value}>{y.label} (Age {y.age})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Gender *</Label>
            <Select 
              value={directStudentData.gender} 
              onValueChange={(v) => setDirectStudentData({ ...directStudentData, gender: v })}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map((g) => (
                  <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Learning Goal *</Label>
          <RadioGroup
            value={directStudentData.learningGoal}
            onValueChange={(v) => setDirectStudentData({ ...directStudentData, learningGoal: v })}
            className="flex gap-4"
          >
            {LEARNING_GOALS.map((goal) => (
              <div key={goal.value} className="flex items-center space-x-2">
                <RadioGroupItem value={goal.value} id={`direct-${goal.value}`} />
                <Label htmlFor={`direct-${goal.value}`} className="text-sm font-normal cursor-pointer">
                  {goal.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        {directStudentData.learningGoal === 'exam-prep' && (
          <div className="space-y-1">
            <Label className="text-sm">Exam Target *</Label>
            <Select 
              value={directStudentData.examTarget} 
              onValueChange={(v) => setDirectStudentData({ ...directStudentData, examTarget: v })}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Select exam" />
              </SelectTrigger>
              <SelectContent>
                {EXAM_OPTIONS.map((e) => (
                  <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );

  // Tutor Registration Form
  const renderTutorForm = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Tutor Registration</h3>
        <p className="text-gray-500 text-sm">Join our growing tutor network</p>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">Full Name *</Label>
            <Input
              type="text"
              placeholder="Enter your full name"
              value={tutorData.fullName}
              onChange={(e) => setTutorData({ ...tutorData, fullName: e.target.value })}
              data-testid="tutor-fullname"
              className="h-10"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Email Address *</Label>
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={tutorData.email}
              onChange={(e) => setTutorData({ ...tutorData, email: e.target.value })}
              data-testid="tutor-email"
              className="h-10"
            />
          </div>
        </div>
        
        <div className="space-y-1">
          <Label className="text-sm">Mobile Number (Optional)</Label>
          <Input
            type="tel"
            placeholder="+44 7XXX XXXXXX"
            value={tutorData.mobile}
            onChange={(e) => setTutorData({ ...tutorData, mobile: e.target.value })}
            className="h-10"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">Password *</Label>
            <Input
              type="password"
              placeholder="Min. 6 characters"
              value={tutorData.password}
              onChange={(e) => setTutorData({ ...tutorData, password: e.target.value })}
              className="h-10"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Confirm Password *</Label>
            <Input
              type="password"
              placeholder="Confirm password"
              value={tutorData.confirmPassword}
              onChange={(e) => setTutorData({ ...tutorData, confirmPassword: e.target.value })}
              className="h-10"
            />
          </div>
        </div>
        
        <div className="space-y-1">
          <Label className="text-sm">Subjects You Teach *</Label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="e.g., Mathematics, English"
              value={newTutorSubject}
              onChange={(e) => setNewTutorSubject(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newTutorSubject.trim()) {
                  e.preventDefault();
                  setTutorData({ ...tutorData, subjects: [...tutorData.subjects, newTutorSubject.trim()] });
                  setNewTutorSubject('');
                }
              }}
              className="h-10"
            />
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (newTutorSubject.trim()) {
                  setTutorData({ ...tutorData, subjects: [...tutorData.subjects, newTutorSubject.trim()] });
                  setNewTutorSubject('');
                }
              }}
              className="h-10 px-3 bg-purple-600"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {tutorData.subjects.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {tutorData.subjects.map((subject, idx) => (
                <Badge key={idx} variant="secondary" className="bg-purple-100 text-purple-700 flex items-center gap-1">
                  {subject}
                  <button
                    type="button"
                    onClick={() => setTutorData({
                      ...tutorData,
                      subjects: tutorData.subjects.filter((_, i) => i !== idx)
                    })}
                    className="hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">Qualifications *</Label>
            <Input
              type="text"
              placeholder="e.g., BSc, PGCE"
              value={tutorData.qualifications}
              onChange={(e) => setTutorData({ ...tutorData, qualifications: e.target.value })}
              className="h-10"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Years of Experience *</Label>
            <Input
              type="number"
              placeholder="e.g., 5"
              min="0"
              value={tutorData.experience}
              onChange={(e) => setTutorData({ ...tutorData, experience: e.target.value })}
              className="h-10"
            />
          </div>
        </div>
        
        <div className="space-y-1">
          <Label className="text-sm">Brief Bio (Optional)</Label>
          <Input
            type="text"
            placeholder="Tell students about yourself..."
            value={tutorData.bio}
            onChange={(e) => setTutorData({ ...tutorData, bio: e.target.value })}
            className="h-10"
          />
        </div>
      </div>
    </div>
  );

  // Final step: Terms and conditions
  const renderTermsStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Almost Done!</h3>
        <p className="text-gray-500 text-sm">Review and accept terms</p>
      </div>
      
      {registrationType === 'parent' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
          <h4 className="font-medium text-purple-800">Registration Summary</h4>
          <div className="text-sm text-purple-700">
            <p><strong>Parent:</strong> {parentData.fullName} ({parentData.email})</p>
            <p className="mt-2"><strong>Students ({students.length}):</strong></p>
            <ul className="list-disc list-inside ml-2">
              {students.map((s, i) => (
                <li key={i}>{s.fullName} - {s.yearGroup} ({s.username})</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      {registrationType === 'tutor' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
          <h4 className="font-medium text-green-800">Tutor Registration Summary</h4>
          <div className="text-sm text-green-700">
            <p><strong>Name:</strong> {tutorData.fullName}</p>
            <p><strong>Email:</strong> {tutorData.email}</p>
            <p><strong>Subjects:</strong> {tutorData.subjects.join(', ')}</p>
            <p><strong>Experience:</strong> {tutorData.experience} years</p>
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
          <Checkbox 
            id="terms-accepted" 
            checked={termsAccepted} 
            onCheckedChange={setTermsAccepted}
            data-testid="terms-checkbox"
          />
          <div className="grid gap-1 leading-none">
            <label htmlFor="terms-accepted" className="text-sm font-medium cursor-pointer">
              Terms & Conditions *
            </label>
            <p className="text-xs text-gray-500">
              I agree to the <a href="/terms" className="text-purple-600 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-purple-600 hover:underline">Privacy Policy</a>.
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
          <Shield className="w-6 h-6 text-green-600 flex-shrink-0" />
          <p className="text-xs text-green-800">
            <span className="font-semibold">Your data is safe.</span> We use industry-standard encryption to protect your information.
          </p>
        </div>
      </div>
    </div>
  );

  // Render current step
  const renderCurrentStep = () => {
    if (registrationType === 'parent') {
      switch (registrationStep) {
        case 1: return renderStep1();
        case 2: return renderParentDetails();
        case 3: return renderStudentDetails();
        case 4: return renderTermsStep();
        default: return renderStep1();
      }
    } else if (registrationType === 'student') {
      switch (registrationStep) {
        case 1: return renderStep1();
        case 2: return renderDirectStudentForm();
        case 3: return renderTermsStep();
        default: return renderStep1();
      }
    } else if (registrationType === 'tutor') {
      switch (registrationStep) {
        case 1: return renderStep1();
        case 2: return renderTutorForm();
        case 3: return renderTermsStep();
        default: return renderStep1();
      }
    }
    return renderStep1();
  };

  // Get button text
  const getButtonText = () => {
    if (loading) return 'Please wait...';
    if (registrationStep === 1) return 'Continue';
    if (registrationStep === getTotalSteps()) return 'Create Account';
    return 'Next';
  };

  // Success modal close handler
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/parent-dashboard');
  };

  // Pending modal close handler  
  const handlePendingModalClose = () => {
    setShowPendingModal(false);
    localStorage.clear();
    setProgress(null);
    navigate('/');
  };

  return (
    <div className="min-h-screen flex">
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animated-gradient {
          background: linear-gradient(-45deg, #667eea, #764ba2, #6B73FF, #8E54E9, #667eea);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
        }
      `}</style>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={handleSuccessModalClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center">Registration Successful!</DialogTitle>
            <DialogDescription className="text-center text-base mt-4">
              Your account and student profiles have been created successfully.
            </DialogDescription>
          </DialogHeader>
          {registeredStudents.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <h4 className="font-medium text-gray-800 mb-2">Student Login Details:</h4>
              {registeredStudents.map((s, i) => (
                <div key={i} className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">{s.fullName}:</span> {s.username}
                </div>
              ))}
              <p className="text-xs text-gray-500 mt-2">Students can change their passwords after first login.</p>
            </div>
          )}
          <div className="mt-6 flex justify-center">
            <Button onClick={handleSuccessModalClose} className="bg-gradient-to-r from-purple-600 to-indigo-600">
              Go to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Approval Modal */}
      <Dialog open={showPendingModal} onOpenChange={handlePendingModalClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-10 h-10 text-yellow-600" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center">Registration Pending</DialogTitle>
            <DialogDescription className="text-center text-base mt-4">
              Your registration is pending approval. We'll notify you once your account is activated.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 flex justify-center">
            <Button onClick={handlePendingModalClose} className="bg-gradient-to-r from-purple-600 to-indigo-600">
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Verification Modal */}
      <Dialog open={showEmailVerificationModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
                <Mail className="w-10 h-10 text-purple-600" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center">Verify Your Email</DialogTitle>
            <DialogDescription className="text-center text-base mt-4">
              We've sent a verification link to:
              <br />
              <span className="font-semibold text-purple-600">{verificationEmail}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="bg-purple-50 rounded-lg p-4 mt-4">
            <p className="text-sm text-purple-800 text-center">
              Please click the link in the email to activate your account. The link will expire in 24 hours.
            </p>
          </div>
          {registeredStudents.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <h4 className="font-medium text-gray-800 mb-2 text-sm">Your children's accounts:</h4>
              {registeredStudents.map((s, i) => (
                <div key={i} className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">{s.fullName}</span> ({s.username})
                </div>
              ))}
              <p className="text-xs text-gray-500 mt-2">Login details will be sent to your email after verification.</p>
            </div>
          )}
          <div className="mt-6 flex flex-col gap-3">
            <Button 
              variant="outline"
              onClick={async () => {
                setResendingEmail(true);
                try {
                  const r = await axios.post(`${API}/auth/resend-verification`, { email: verificationEmail });
                  if (r.data?.emailSent === false) {
                    toast.warning(r.data?.message || "Email queued but not yet delivered. Check spam in a minute.");
                  } else {
                    toast.success(r.data?.message || 'Verification email sent — check your inbox (and spam folder).');
                  }
                } catch (error) {
                  const status = error.response?.status;
                  const detail = error.response?.data?.detail || 'Failed to resend email';
                  if (status === 429) {
                    toast.warning(detail);
                  } else if (status === 502) {
                    toast.error(detail, { duration: 7000 });
                  } else {
                    toast.error(detail);
                  }
                }
                setResendingEmail(false);
              }}
              disabled={resendingEmail}
              className="w-full"
            >
              {resendingEmail ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            <Button 
              onClick={() => {
                setShowEmailVerificationModal(false);
                setActiveTab('signin');
                setRegistrationStep(1);
                setRegistrationType('');
              }} 
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
            >
              Go to Sign In
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-lg">
          <Card className="w-full shadow-xl border-0" data-testid="auth-card">
            <CardHeader className="text-center space-y-2 pb-4">
              <div className="flex justify-center mb-2 lg:hidden">
                <EdpiesLogo size="lg" />
              </div>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                {activeTab === 'signin' ? 'Welcome Back!' : 'Join Edpies'}
              </CardTitle>
              <CardDescription className="text-sm text-gray-600">
                {activeTab === 'signin' 
                  ? 'Sign in to continue your learning journey' 
                  : 'Create your account to get started'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setRegistrationStep(1); setRegistrationType(''); }}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="signin" data-testid="tab-signin">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
                </TabsList>
                
                <TabsContent value="signin">
                  {!showForgotPassword ? (
                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-1">
                        <Label htmlFor="signin-email" className="text-sm">Email / Username</Label>
                        <Input 
                          id="signin-email" 
                          type="text" 
                          placeholder="your.email@example.com or username" 
                          value={signInEmail} 
                          onChange={(e) => setSignInEmail(e.target.value)} 
                          required 
                          data-testid="signin-email" 
                          className="h-10" 
                        />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="signin-password" className="text-sm">Password</Label>
                          <button 
                            type="button" 
                            onClick={() => setShowForgotPassword(true)} 
                            className="text-xs text-purple-600 hover:underline"
                          >
                            Forgot password?
                          </button>
                        </div>
                        <Input 
                          id="signin-password" 
                          type="password" 
                          placeholder="Enter your password" 
                          value={signInPassword} 
                          onChange={(e) => setSignInPassword(e.target.value)} 
                          required 
                          data-testid="signin-password" 
                          className="h-10" 
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full h-10 font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700" 
                        disabled={loading}
                        data-testid="signin-button"
                      >
                        {loading ? 'Signing in...' : 'Sign In'}
                      </Button>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <button 
                        type="button" 
                        onClick={() => { setShowForgotPassword(false); setForgotPasswordStep(1); }} 
                        className="flex items-center text-sm text-purple-600 hover:text-purple-700"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Sign In
                      </button>
                      <div className="text-center mb-4">
                        <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <KeyRound className="w-7 h-7 text-purple-600" />
                        </div>
                        <h3 className="text-lg font-semibold">{forgotPasswordStep === 1 ? 'Reset Password' : 'Enter Reset Code'}</h3>
                      </div>
                      {forgotPasswordStep === 1 ? (
                        <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                          <div className="space-y-1">
                            <Label className="text-sm">Email Address</Label>
                            <Input 
                              type="email" 
                              placeholder="your.email@example.com" 
                              value={forgotEmail} 
                              onChange={(e) => setForgotEmail(e.target.value)} 
                              required 
                              className="h-10" 
                            />
                          </div>
                          <Button type="submit" className="w-full h-10 bg-gradient-to-r from-purple-600 to-indigo-600" disabled={loading}>
                            {loading ? 'Sending...' : 'Send Reset Code'}
                          </Button>
                        </form>
                      ) : (
                        <form onSubmit={handleResetPassword} className="space-y-3">
                          <div className="space-y-1">
                            <Label className="text-sm">Reset Code</Label>
                            <Input 
                              type="text" 
                              placeholder="6-digit code" 
                              value={resetCode} 
                              onChange={(e) => setResetCode(e.target.value)} 
                              required 
                              className="h-10 text-center tracking-widest" 
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm">New Password</Label>
                            <Input 
                              type="password" 
                              placeholder="Min. 6 characters" 
                              value={newPassword} 
                              onChange={(e) => setNewPassword(e.target.value)} 
                              required 
                              className="h-10" 
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm">Confirm Password</Label>
                            <Input 
                              type="password" 
                              placeholder="Confirm password" 
                              value={confirmNewPassword} 
                              onChange={(e) => setConfirmNewPassword(e.target.value)} 
                              required 
                              className="h-10" 
                            />
                          </div>
                          <Button type="submit" className="w-full h-10 bg-gradient-to-r from-purple-600 to-indigo-600" disabled={loading}>
                            {loading ? 'Resetting...' : 'Reset Password'}
                          </Button>
                        </form>
                      )}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="register">
                  {registrationType && registrationStep > 1 && <ProgressIndicator />}
                  <form onSubmit={handleRegisterSubmit} className="space-y-4">
                    {renderCurrentStep()}
                    <div className="flex gap-2 pt-2">
                      {registrationStep > 1 && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setRegistrationStep(prev => prev - 1)} 
                          className="flex-1 h-10"
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" /> Back
                        </Button>
                      )}
                      <Button 
                        type="submit" 
                        className={`h-10 font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 ${
                          registrationStep === 1 ? 'w-full' : 'flex-1'
                        }`}
                        disabled={loading || (registrationStep === 1 && !registrationType)}
                        data-testid="register-submit"
                      >
                        {getButtonText()}
                        {registrationStep < getTotalSteps() && !loading && <ChevronRight className="w-4 h-4 ml-1" />}
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          
          <div className="mt-6 text-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-purple-600 hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 animated-gradient relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-white/20 backdrop-blur-sm mb-6 shadow-2xl">
              <EdpiesLogo size="xl" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Edpies
            </h1>
            <p className="text-xl text-white/90 font-medium">
              Where intelligent learning becomes a way of life!
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 w-full max-w-md">
            {[
              { icon: '🧠', title: 'AI-Powered', desc: 'Adaptive learning paths' },
              { icon: '📚', title: 'All Levels', desc: '7+ to A-Levels' },
              { icon: '👨‍👩‍👧‍👦', title: 'Family Plans', desc: 'Multiple children' },
              { icon: '📊', title: 'Progress', desc: 'Detailed analytics' },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-white">
                <span className="text-2xl">{item.icon}</span>
                <h3 className="font-semibold mt-2">{item.title}</h3>
                <p className="text-sm text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAuth;
